/**
 * Numeración correlativa de los equipos DEA de la lámina.
 *
 * Los equipos en altura continúan la numeración de terreno (D3, D4…): para el
 * cliente son una sola dotación, no dos listas separadas. Un nivel puede tener
 * varios equipos, y si comparte con terreno el PRIMERO reutiliza ese número
 * —es el mismo aparato— y los adicionales son nuevos.
 */

import { clientDeaCount, hasClientDea, isSharedLevel, levelsBottomUp, sharedDeaNumber } from './compliance.js';

/**
 * Asigna números a todos los equipos verticales de todos los edificios.
 *
 * Se recorre entero aunque se pregunte por un solo nivel: el número de un equipo
 * depende de cuántos vinieron antes, así que no se puede calcular aislado.
 *
 * @param {object[]} buildings
 * @param {number} groundCount Cantidad de DEA de terreno ya numerados.
 * @returns {Map<string, number[]>} Clave `"<índiceEdificio>:<nivel>"` → números.
 */
export function numberVerticalEquipment(buildings, groundCount) {
  const numbers = new Map();
  let counter = groundCount;

  buildings.forEach((building, buildingIndex) => {
    // De arriba hacia abajo, como se lee un corte de edificio.
    const levels = levelsBottomUp(building).slice().reverse();
    levels.forEach((level) => {
      if (!hasClientDea(building, level)) return;
      const count = clientDeaCount(building, level);
      const assigned = [];

      if (isSharedLevel(building, level)) {
        assigned.push(sharedDeaNumber(building, level));
        for (let i = 1; i < count; i++) assigned.push(++counter);
      } else {
        for (let i = 0; i < count; i++) assigned.push(++counter);
      }

      numbers.set(`${buildingIndex}:${level}`, assigned);
    });
  });

  return numbers;
}

/** Números de los equipos de un nivel concreto. Arreglo vacío si no hay. */
export function equipmentNumbers(numbering, buildingIndex, level) {
  return numbering.get(`${buildingIndex}:${level}`) || [];
}

/**
 * Etiqueta del nivel para la lámina.
 *
 * Con un solo nombre para el nivel se agrupan los números y el nombre va una
 * vez ("D2, D7 · NIVEL 1"). Con un nombre por equipo se emparejan uno a uno
 * ("D2 · Foodtruck, D3 · Cinta"). La diferencia importa: agrupar dos equipos
 * con nombres distintos borraría dónde está cada uno.
 */
export function equipmentLabel(numbers, names) {
  const labels = Array.isArray(names) ? names : names ? [names] : [];

  if (labels.length > 1 && labels.length === numbers.length) {
    return numbers.map((n, i) => `D${n} · ${labels[i].toUpperCase()}`).join(', ');
  }

  const joined = numbers.map((n) => `D${n}`).join(', ');
  return labels.length ? `${joined} · ${labels[0].toUpperCase()}` : joined;
}
